//React imports
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
//---

//CSS imports
import './ModelTree.css'
//---

//PrimeReact imports
import { useForm, Controller } from 'react-hook-form';
import { Tree } from 'primereact/tree';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { ConfirmDialog } from 'primereact/confirmdialog';
//---

//Vendors imports
import axios from 'axios';
//---

//Components imports
import { useNotification } from '../NotificationProvider';
//---

//Utils imports
import { parseDashInPath } from '../../utils/Parser';
//---

//Data requests imports
import {
    deleteDbtModel,
    createDbtModel,
    checkDbtModelLock,
    closeDbtModel
} from '../../data/DbtModelData';
import {
    getAuthtokenClaim
} from '../../data/LoginData';
import {
    defaultTreeNode
} from '../../data/DefaultStates';
//---

const ModelTree = ({ projectName, models, listDbtModels, className }) => {
    const history = useHistory();

    const cancelTokenSource = axios.CancelToken.source();

    const { showNotification } = useNotification();

    const [selectedKey, setSelectedKey] = useState(null);
    const [selectedNode, setSelectedNode] = useState(defaultTreeNode);
    const [expandedKeys, setExpandedKeys] = useState({ 1: true });

    const defaultValues = {
        modelName: ''
    }

    const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

    const [displayForm, setDisplayForm] = useState(false);

    const [confirmDialogState, setConfirmDialogState] = useState({
        visible: false,
        message: '',
        footer: null
    });

    const openDbtModelCtlr = (projectName, modelName) => {
        checkDbtModelLock(cancelTokenSource, projectName, modelName).then(
            data => {
                if (data.dbtFileSession) {
                    let sub = getAuthtokenClaim('sub')
                    if (sub) {
                        if (sub !== data.dbtFileSession['locked-by']) {
                            let message = <div>
                                The user <span style={{ fontWeight: 'bold' }}>{data.dbtFileSession['locked-by']}</span>
                                &nbsp;currently has a work session open on this model.<br />
                                You can <span style={{ fontWeight: 'bold' }}>continue</span> his session and take back his code
                                or <span style={{ fontWeight: 'bold' }}>overwrite</span> it and start again with a clean code.
                            </div>

                            setConfirmDialogState({
                                visible: true,
                                message: message,
                                footer: renderOpenConfirmDialogFooter(data.dbtFileSession['redis-id'])
                            });
                        } else {
                            goToModel('open');
                        }
                    } else {
                        showNotification('error', 'Error', 'invalid token, try to log in again or contact an administrator', 6000)
                        return;
                    }
                } else {
                    goToModel('open');
                }
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const checkAndDeleteDbtModelCtlr = (modelName) => {
        checkDbtModelLock(cancelTokenSource, projectName, modelName).then(
            data => {
                if (data.dbtFileSession) {
                    let sub = getAuthtokenClaim('sub')
                    if (sub) {
                        if (sub !== data.dbtFileSession['locked-by']) {
                            let message = <div>
                                The user <span style={{ fontWeight: 'bold' }}>{data.dbtFileSession['locked-by']}</span>
                                &nbsp;currently has a work session open on this model.<br />
                                If you delete the source, his current work will be lost.
                            </div>

                            setConfirmDialogState({
                                visible: true,
                                message: message,
                                footer: renderDeleteConfirmDialogFooter
                            });
                        } else {
                            deleteDbtModelCtlr(modelName);
                        }
                    } else {
                        showNotification('error', 'Error', 'invalid token, try to log in again or contact an administrator', 6000)
                        return;
                    }
                } else {
                    deleteDbtModelCtlr(modelName);
                }
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const closeAndOpenDbtModelCtlr = (dbtFileSessionRedisID) => {
        closeDbtModel(cancelTokenSource, projectName, dbtFileSessionRedisID).then(
            () => {
                goToModel('open')
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const deleteDbtModelCtlr = (modelName) => {
        deleteDbtModel(cancelTokenSource, projectName, modelName).then(
            () => {
                listDbtModels()
                showNotification('success', 'Success', 'model successfully deleted', 6000)
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const createDbtModelCtlr = (formData) => {
        if (selectedNode.type !== 'dir') {
            let message = <div>select a folder <i className="pi pi-folder" /> as location</div>
            showNotification('info', 'Info', message, 6000)
            return
        }

        let fullModelName = selectedNode.fullPath + formData.modelName

        createDbtModel(cancelTokenSource, projectName, fullModelName).then(
            data => {
                listDbtModels()
                reset()
                showNotification('success', 'Success', 'model successfully created', 6000)
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const onDelete = (currentTarget) => {
        let message = <div>
            Do you want to delete the <span style={{ fontWeight: 'bold' }}>{selectedNode.fullPath}</span> model ?
        </div>

        confirmPopup({
            target: currentTarget,
            message: message,
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            accept: () => checkAndDeleteDbtModelCtlr(selectedNode.fullPath),
        });
    }

    const goToModel = (mode) => {
        history.push('/' + projectName + "/transform/model/" + mode + "/" + parseDashInPath(selectedNode.fullPath) + "/")
    }

    const getFormErrorMessage = (name) => {
        return errors[name] && <small className='p-error'>{errors[name].message}</small>
    };

    const modelForm = () => {
        return (
            <div className='form-container p-mb-3'>
                <form onSubmit={handleSubmit(createDbtModelCtlr)} autoComplete='on'>
                    <div className='p-field'>
                        <Controller name='modelName' control={control} rules={{ required: 'Name is required.' }} render={({ field, fieldState }) => (
                            <InputText
                                id={field.name}
                                {...field}
                                autoFocus
                                className={classNames('p-d-block model-name-input', { 'p-invalid': fieldState.invalid })}
                            />
                        )} />
                        <small id="modelName-help" className="p-d-block">Enter the model name.</small>
                        {getFormErrorMessage('modelName')}
                    </div>

                </form>
                <div className='buttons-container'>
                    <Button label="Cancel" icon="pi pi-times" onClick={() => { setDisplayForm(false); reset(); }} className="p-button-outlined p-mr-2" />
                    <Button label="Create" icon="pi pi-check" onClick={handleSubmit(createDbtModelCtlr)} />
                </div>
            </div>
        )
    }

    const renderOpenConfirmDialogFooter = (dbtFileSessionRedisID) => {
        return (
            <div>
                <Button label="Back" icon="pi pi-arrow-left" className="p-button-text"
                    onClick={() => setConfirmDialogState({ visible: false, message: '', footer: null })} />
                <Button label="Overwrite" icon="pi pi-times" className="p-button-danger"
                    onClick={() => closeAndOpenDbtModelCtlr(dbtFileSessionRedisID)} />
                <Button label="Continue" icon="pi pi-check"
                    onClick={() => goToModel('open')} />
            </div>
        );
    };

    const renderDeleteConfirmDialogFooter = () => {
        return (
            <div>
                <Button label="Back" icon="pi pi-arrow-left" className="p-button-text"
                    onClick={() => setConfirmDialogState({ visible: false, message: '', footer: null })} />
                <Button label="Delete" icon="pi pi-times" className="p-button-danger"
                    onClick={() => deleteDbtModelCtlr(selectedNode.fullPath)} />
            </div>
        );
    };

    const treeHeader = () => {
        const leftContents = (
            <React.Fragment>
                <Button
                    label="New"
                    icon="pi pi-plus"
                    disabled={selectedNode.type === 'dir' ? false : true}
                    onClick={() => setDisplayForm(true)}
                />
                <i className="pi pi-bars p-toolbar-separator p-mr-2" />
                <Button
                    label="Open"
                    icon="pi pi-external-link"
                    className="p-button p-mr-2"
                    disabled={selectedNode.type === 'file' ? false : true}
                    onClick={() => openDbtModelCtlr(projectName, selectedNode.fullPath)}
                />
                <Button
                    label="Show"
                    icon="pi pi-eye"
                    className="p-button p-mr-2"
                    disabled={selectedNode.type === 'file' ? false : true}
                    onClick={() => goToModel('read')}
                />
                <Button
                    label="Delete"
                    icon="pi pi-times"
                    className="p-button-outlined p-button-danger"
                    disabled={selectedNode.type === 'file' ? false : true}
                    onClick={(event) => onDelete(event.currentTarget)}
                />
            </React.Fragment>
        );

        return (
            <div>
                <Toolbar left={leftContents} />
                {
                    displayForm ?
                        modelForm() :
                        null
                }
            </div>
        )
    }

    return (
        <div
            className={
                classNames('p-d-flex p-jc-center p-ai-center model-tree', displayForm ? 'small-model-tree' : 'model-tree', className)
            }
        >
            <ConfirmDialog
                header='Confirmation'
                message={confirmDialogState.message}
                icon='pi pi-exclamation-triangle'
                footer={confirmDialogState.footer}
                visible={confirmDialogState.visible}
                onHide={() => setConfirmDialogState({
                    visible: false,
                    message: '',
                    footer: null
                })}
            />
            <ConfirmPopup />
            <Tree
                value={models}
                selectionMode='single'
                selectionKeys={selectedKey}
                onSelectionChange={e => setSelectedKey(e.value)}
                onSelect={e => setSelectedNode(e.node)}
                expandedKeys={expandedKeys}
                onToggle={e => setExpandedKeys(e.value)}
                header={treeHeader}
            />
        </div>
    );
};

export default ModelTree;
